/**
 * Scale drop form state and handlers.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { toClickIdStructure, toDomainScheme } from '@trafficpulse/shared';
import { useCreateGatewaySenderTemplate } from '@/hooks';
import {
  MAX_SELECTED_TEXTS,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import type { DropApplyValues } from '@/stores/drop-apply.store';
import { getDefaultTldValue } from '@/features/smswiser-campaigns/utils/tldDefaults';
import { parseIntervalHHMM } from '@/features/smswiser-campaigns/utils/scheduleInterval';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import type { UseScaleDropFormParams } from './ScaleDropModal.types';
import { useScaleDropFormDerived } from './hooks/useScaleDropFormDerived';
import { useScaleDropFormTemplateSync } from './hooks/useScaleDropFormTemplateSync';
import { useScaleDropFormHandlers } from './hooks/useScaleDropFormHandlers';
import { useScaleDropFormTrackedSetters } from './hooks/useScaleDropFormTrackedSetters';
import { useScaleDropFormConfirm } from './hooks/useScaleDropFormConfirm';
import { useScaleDropFormSyncFromRows } from './hooks/useScaleDropFormSyncFromRows';
import { computeScaleDropCostInfo } from './utils/scaleDropFormCostInfo';

export function useScaleDropForm(params: UseScaleDropFormParams) {
  const { countryKey, rows, gateways, leadsLists, tlds, senderNames, smsTexts, defaultWorkDate, titlePrefix, mode = 'scale', editDropId, editDrops, onAddSender, onAddText, onDeleteSender, onDeleteText, onDeleteTextsBulk, onConfirm, onSave, onCreateFromLists, createFromListsWiserId } = params;

  const derived = useScaleDropFormDerived(rows, gateways, leadsLists, tlds);
  const { drop, isMulti, routeMatch, leadsMatch, tldMatch, mergedSendersFromRows, mergedTextsFromRows, allDropsHaveGateway, defaultGatewayForMulti, defaultStaticTagIdFromRows } = derived;

  const KEEP_EACH_ROUTE = -1;
  const [localSenders, setLocalSenders] = useState<string[]>([]);
  const [localTexts, setLocalTexts] = useState<string[]>([]);
  const [newSenderValue, setNewSenderValue] = useState('');
  const [newTextValue, setNewTextValue] = useState('');
  const [routeId, setRouteId] = useState(() => (!isMulti ? routeMatch?.id ?? 0 : allDropsHaveGateway ? KEEP_EACH_ROUTE : defaultGatewayForMulti));
  const [tldValue, setTldValue] = useState(isMulti ? getDefaultTldValue(tlds) : (tldMatch?.value ?? getDefaultTldValue(tlds)));
  const [amount, setAmount] = useState<number | ''>(drop?.cap ?? 500);
  const [leadsListWiserId, setLeadsListWiserId] = useState(leadsMatch?.id ?? 0);
  const [executeAfter, setExecuteAfter] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [selectedSenderNames, setSelectedSenderNames] = useState<string[]>(isMulti ? [] : mergedSendersFromRows);
  const [selectedSmsTexts, setSelectedSmsTexts] = useState<string[]>(isMulti ? [] : mergedTextsFromRows.slice(0, MAX_SELECTED_TEXTS));
  const [senderSearch, setSenderSearch] = useState('');
  const [textSearch, setTextSearch] = useState('');
  const [showLeads, setShowLeads] = useState(true);
  const [showSenders, setShowSenders] = useState(true);
  const [showTexts, setShowTexts] = useState(true);

  const ed = editDrops?.[0];
  const isEdit = mode === 'edit';
  const [forceSend, setForceSend] = useState(ed?.forceSend ?? false);
  const [sendInBatches, setSendInBatches] = useState(Boolean(ed?.batchAmount));
  const [batchAmount, setBatchAmount] = useState<number | undefined>(ed?.batchAmount ?? undefined);
  const [batchDelay, setBatchDelay] = useState<number | undefined>(ed?.batchDelay ?? undefined);
  const [useOnlyNewDomains, setUseOnlyNewDomains] = useState(ed?.useOnlyNewDomains ?? false);
  // Scaling one sent drop keeps its shortlink format; multi-row scale starts from the defaults.
  const shortlinkSource = ed ?? (isMulti ? undefined : drop);
  const [clickIdStructure, setClickIdStructure] = useState(() => toClickIdStructure(shortlinkSource?.clickIdStructure));
  const [domainScheme, setDomainScheme] = useState(() => toDomainScheme(shortlinkSource?.domainScheme));
  const [selectedStaticTagId, setSelectedStaticTagId] = useState<string | null>(
    isEdit ? (ed?.staticTagId ?? null) : (defaultStaticTagIdFromRows ?? null),
  );
  const [scheduleInterval, setScheduleInterval] = useState('');
  const scheduleIntervalMinutes = parseIntervalHHMM(scheduleInterval);
  const [pendingHideTexts, setPendingHideTexts] = useState<string[]>([]);
  const [replacedByAiTexts, setReplacedByAiTexts] = useState<string[]>([]);
  const [perDropSmsTexts, setPerDropSmsTexts] = useState<string[][] | null>(null);
  const [pendingHideSenders, setPendingHideSenders] = useState<string[]>([]);
  const [replacedByAiSenders, setReplacedByAiSenders] = useState<string[]>([]);
  const [perDropSenderNames, setPerDropSenderNames] = useState<string[][] | null>(null);
  const markTextsForHide = useCallback(
    (texts: string[]) => setPendingHideTexts((p) => [...p, ...texts]),
    [],
  );
  const markSendersForHide = useCallback(
    (senders: string[]) => setPendingHideSenders((p) => [...p, ...senders]),
    [],
  );

  const effectiveRouteIdForTemplates = routeId > 0 ? routeId : 0;
  const templateSync = useScaleDropFormTemplateSync(countryKey, effectiveRouteIdForTemplates, selectedSenderNames);
  const { activeTemplateId, setActiveTemplateId, senderTemplates, lastSyncedRef } = templateSync;
  const createTemplate = useCreateGatewaySenderTemplate(countryKey, effectiveRouteIdForTemplates);

  const sendersList = useMemo(() => uniqueKeepOrderStrings([...senderNames, ...mergedSendersFromRows, ...localSenders]), [senderNames, mergedSendersFromRows, localSenders]);
  // Include the drop's own saved texts so they remain visible even if removed from the global creative list.
  const textsList = useMemo(() => uniqueKeepOrderStrings([...smsTexts, ...mergedTextsFromRows, ...localTexts]), [smsTexts, mergedTextsFromRows, localTexts]);

  const hasUserEditedRef = useRef(false);
  useScaleDropFormSyncFromRows(rows, isMulti, routeMatch ?? null, leadsMatch ?? null, tldMatch ?? null, mergedSendersFromRows, mergedTextsFromRows, smsTexts, allDropsHaveGateway, defaultGatewayForMulti, defaultStaticTagIdFromRows ?? null, { setAmount, setRouteId, setLeadsListWiserId, setTldValue, setSelectedSenderNames, setSelectedSmsTexts, setSelectedStaticTagId }, hasUserEditedRef);

  const tracked = useScaleDropFormTrackedSetters(hasUserEditedRef, {
    setAmount,
    setRouteId,
    setTldValue,
    setLeadsListWiserId,
    setSelectedSenderNames,
    setSelectedSmsTexts,
    setSelectedStaticTagId,
    setLocalSenders,
    setLocalTexts,
  });

  const selectedGateway = useMemo(() => gateways.find((g) => g.id === routeId), [gateways, routeId]);
  const costInfo = useMemo(
    () => computeScaleDropCostInfo(selectedGateway?.pricePerSms, amount, isMulti, rows.length),
    [selectedGateway?.pricePerSms, amount, isMulti, rows.length],
  );

  const filteredSenders = sendersList.filter((s) => s.toLowerCase().includes(senderSearch.toLowerCase()));
  const filteredTexts = textsList.filter((t) => t.toLowerCase().includes(textSearch.toLowerCase()));

  const handlers = useScaleDropFormHandlers({
    sendersList,
    filteredSenders,
    filteredTexts,
    newSenderValue,
    newTextValue,
    selectedSenderNames,
    selectedSmsTexts,
    effectiveRouteIdForTemplates,
    setSelectedSenderNames: tracked.setSelectedSenderNamesTracked,
    setSelectedSmsTexts: tracked.setSelectedSmsTextsTracked,
    setLocalSenders: tracked.setLocalSendersTracked,
    setLocalTexts: tracked.setLocalTextsTracked,
    setReplacedByAiTexts,
    setPerDropSmsTexts,
    setReplacedByAiSenders,
    setPerDropSenderNames,
    setNewSenderValue,
    setNewTextValue,
    setActiveTemplateId,
    lastSyncedRef,
    createTemplate,
    onAddSender,
    onAddText,
    onDeleteSender,
    onDeleteText,
    onDeleteTextsBulk,
  });

  const confirm = useScaleDropFormConfirm({
    amount,
    routeId,
    tldValue,
    executeAfter,
    selectedSenderNames,
    selectedSmsTexts,
    perDropSmsTexts,
    perDropSenderNames,
    leadsListWiserId: leadsListWiserId ?? 0,
    isMulti,
    mode: mode === 'edit' ? 'edit' : mode === 'create-from-lists' ? 'create-from-lists' : 'scale',
    editDropId,
    defaultWorkDate,
    titlePrefix,
    routeMatch: routeMatch ?? null,
    tldMatch: tldMatch ?? null,
    tlds,
    rows,
    leadsLists,
    leadsMatch: leadsMatch ?? null,
    allDropsHaveGateway,
    setLeadsListWiserIdTracked: tracked.setLeadsListWiserIdTracked,
    onSave,
    onConfirm: onConfirm as (p: unknown, r?: DropsWithStatsRow[]) => void | Promise<void>,
    onCreateFromLists: onCreateFromLists as ((p: unknown, r?: DropsWithStatsRow[]) => void | Promise<void>) | undefined,
    createFromListsWiserId,
    forceSend,
    sendInBatches,
    batchAmount,
    batchDelay,
    useOnlyNewDomains,
    clickIdStructure,
    domainScheme,
    scheduleIntervalMinutes,
    selectedStaticTagId,
  });

  const allowKeepEachGateway = isMulti && allDropsHaveGateway;
  const h = handlers;

  const applyPastedConfig = useCallback(
    (values: DropApplyValues) => {
      if (values.gatewayName) {
        const gw = gateways.find(
          (g) => g.name?.trim().toLowerCase() === values.gatewayName!.trim().toLowerCase(),
        );
        if (gw) tracked.setRouteIdTracked(gw.id);
      }
      if (values.senderNames?.length) {
        const missing = values.senderNames.filter((s) => !sendersList.includes(s));
        if (missing.length > 0) {
          tracked.setLocalSendersTracked((p) => uniqueKeepOrderStrings([...p, ...missing]));
        }
        tracked.setSelectedSenderNamesTracked(values.senderNames);
      }
      const texts = values.smsTexts?.length ? values.smsTexts : [];
      if (texts.length > 0) {
        const missing = texts.filter((t) => !textsList.includes(t));
        if (missing.length > 0) {
          tracked.setLocalTextsTracked((p) => uniqueKeepOrderStrings([...p, ...missing]));
        }
        tracked.setSelectedSmsTextsTracked(texts.slice(0, MAX_SELECTED_TEXTS));
      }
    },
    [
      gateways,
      sendersList,
      textsList,
      tracked.setRouteIdTracked,
      tracked.setLocalSendersTracked,
      tracked.setSelectedSenderNamesTracked,
      tracked.setLocalTextsTracked,
      tracked.setSelectedSmsTextsTracked,
    ],
  );

  return {
    drop, allowKeepEachGateway, routeId, setRouteId: tracked.setRouteIdTracked, tldValue, setTldValue: tracked.setTldValueTracked,
    amount, setAmount: tracked.setAmountTracked, executeAfter, setExecuteAfter, leadsListWiserId,
    setLeadsListWiserId: tracked.setLeadsListWiserIdTracked, handleLeadSelect: confirm.handleLeadSelect,
    leadSearch, setLeadSearch, selectedSenderNames, selectedSmsTexts, senderSearch, setSenderSearch,
    textSearch, setTextSearch, newSenderValue, setNewSenderValue, newTextValue, setNewTextValue,
    showLeads, setShowLeads, showSenders, setShowSenders, showTexts, setShowTexts,
    sendersList, textsList, costInfo,
    toggleSender: h.toggleSender, toggleText: h.toggleText, selectAllSenders: h.selectAllSenders, selectAllTexts: h.selectAllTexts,
    handleAddSender: h.handleAddSender, handleAddText: h.handleAddText, handleConfirm: confirm.handleConfirm, canConfirm: confirm.canConfirm,
    clearSendersSelection: h.clearSendersSelection, clearTextsSelection: h.clearTextsSelection,
    handleDeleteSender: h.handleDeleteSender, handleDeleteSenders: h.handleDeleteSenders, handleDeleteText: h.handleDeleteText, handleDeleteTextsBulk: h.handleDeleteTextsBulk,
    senderTemplates, activeTemplateId, handleSelectTemplate: h.handleSelectTemplate, handleSaveAsTemplate: h.handleSaveAsTemplate,
    canSaveTemplate: effectiveRouteIdForTemplates > 0,
    forceSend, setForceSend, sendInBatches, setSendInBatches,
    batchAmount, setBatchAmount, batchDelay, setBatchDelay,
    useOnlyNewDomains, setUseOnlyNewDomains,
    clickIdStructure, setClickIdStructure, domainScheme, setDomainScheme,
    selectedStaticTagId, setSelectedStaticTagId: tracked.setSelectedStaticTagIdTracked,
    scheduleInterval, setScheduleInterval, scheduleIntervalMinutes,
    autoSelectTexts: h.autoSelectTexts,
    saveGeneratedAndSelectOnly: h.saveGeneratedAndSelectOnly,
    pendingHideTexts,
    replacedByAiTexts,
    markTextsForHide,
    perDropSmsTexts,
    setPerDropSmsTexts,
    pendingHideSenders,
    replacedByAiSenders,
    markSendersForHide,
    perDropSenderNames,
    setPerDropSenderNames,
    saveGeneratedSendersAndSelectOnly: h.saveGeneratedSendersAndSelectOnly,
    applyPastedConfig,
  };
}
